/**
 * PackageBlock — bloque de un paquete (un destinatario) en la encomienda de trabajo.
 *
 * Decisiones de diseño:
 * - Cabecera editable mínima: destinatario y unique_package_id inline. El ID se
 *   VALIDA (prefijo-guión-número, p. ej. AV-12); un valor inválido muestra error
 *   en rojo suave y NO se propaga — el último ID válido sigue mandando el orden
 *   del Anexo (regla de orden bloqueada, generateAnexoPDF.js).
 * - Chip de tramo de impuesto calculado EN VIVO con calcPackageTax (Tier 1):
 *   dai > 0 → chip 30%, si no → IVA; sin config completa no hay chip.
 *   Nunca se reimplementa la fórmula.
 * - "Agregar artículo" crea un artículo MANUAL (no_client_reference: true, sin
 *   source_message_id): el operador jamás inventa un artículo sin que quede la
 *   marca visible (doctrina cliente-como-verdad, principio 3).
 * - Mover artículo a otro paquete / combinar paquetes: menús inline pequeños,
 *   sin drag-and-drop — el orden de los paquetes NO es manipulable (regla
 *   bloqueada) y el teclado debe bastar.
 * - Encabezado de columnas una sola vez por bloque; las filas son ItemRow.
 */
import { useRef, useState } from 'react';
import { Plus, FolderInput, Merge } from 'lucide-react';
import { packageCrossesDai } from '../../utils/customsCalc';
import { TAX_TIER_META } from './shared/StatusChip';
import ItemRow from './ItemRow';

const ID_PAQUETE_RE = /^[A-ZÁÉÍÓÚÑ0-9]{1,6}-\d{1,4}$/i;

const inputBase =
  'rounded-md border px-2 py-1.5 text-xs focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-[var(--blue)] disabled:opacity-60';

const inputStyle = {
  background: 'var(--input-bg)',
  borderColor: 'var(--border)',
  color: 'var(--text-main)',
};

let contadorItemsNuevos = 0;
const nuevoIdItem = () => {
  contadorItemsNuevos += 1;
  return `it-op-${Date.now()}-${contadorItemsNuevos}`;
};

const PackageBlock = ({
  pkg,
  allPackages,
  customsConfig,
  onChange,
  onMoveItem,
  onMerge,
  quoteBuffer,
  onConsumeQuote,
  onJumpToMessage,
  onAskClient,
  timestampsPorMensaje = {},
}) => {
  const [idBorrador, setIdBorrador] = useState(null); // null = sin edición en curso
  const [idInvalido, setIdInvalido] = useState(false);
  const [menuMoverItemId, setMenuMoverItemId] = useState(null);
  const [confirmarCombinarCon, setConfirmarCombinarCon] = useState('');
  const ultimoAgregadoRef = useRef(null);

  const items = pkg.items || [];
  const subtotal = items.reduce(
    (s, it) => s + (parseFloat(it.unit_value) || 0) * (parseInt(it.quantity) || 1),
    0
  );
  const conDai = packageCrossesDai(items, customsConfig);
  const tierMeta = conDai === null ? null : TAX_TIER_META[conDai ? '30%' : 'IVA'];

  const otrosPaquetes = (allPackages || []).filter(
    (p) => p.unique_package_id !== pkg.unique_package_id
  );

  const confirmarIdPaquete = (valor) => {
    const limpio = valor.trim().toUpperCase();
    if (ID_PAQUETE_RE.test(limpio)) {
      setIdInvalido(false);
      setIdBorrador(null);
      if (limpio !== pkg.unique_package_id) onChange({ ...pkg, unique_package_id: limpio });
    } else {
      setIdInvalido(true);
    }
  };

  const actualizarItem = (itemId, campo, valor) => {
    onChange({
      ...pkg,
      items: items.map((it) => (it.id === itemId ? { ...it, [campo]: valor } : it)),
    });
  };

  const quitarItem = (itemId) => {
    onChange({ ...pkg, items: items.filter((it) => it.id !== itemId) });
  };

  const agregarItemManual = () => {
    const id = nuevoIdItem();
    ultimoAgregadoRef.current = id;
    onChange({
      ...pkg,
      items: [
        ...items,
        {
          id,
          description: '',
          quantity: 1,
          unit_value: 0,
          condition: 'NUEVO',
          no_client_reference: true,
        },
      ],
    });
  };

  return (
    <section
      className="rounded-lg border"
      style={{ background: 'var(--tile)', borderColor: 'var(--border)' }}
      aria-label={`Paquete ${pkg.unique_package_id || 'sin identificador'} — ${pkg.recipient_name || 'sin destinatario'}`}
    >
      {/* ── Cabecera del paquete ── */}
      <div
        className="flex flex-wrap items-center gap-2 border-b px-3 py-2"
        style={{ borderColor: 'var(--border)' }}
      >
        <label className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
          Destinatario
          <input
            type="text"
            value={pkg.recipient_name || ''}
            onChange={(e) => onChange({ ...pkg, recipient_name: e.target.value })}
            aria-label="Nombre del destinatario"
            placeholder="Nombre del destinatario"
            className={`${inputBase} w-48 font-medium`}
            style={inputStyle}
          />
        </label>

        <label className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
          ID
          <input
            type="text"
            value={idBorrador ?? pkg.unique_package_id ?? ''}
            onChange={(e) => {
              setIdBorrador(e.target.value);
              setIdInvalido(false);
            }}
            onBlur={(e) => confirmarIdPaquete(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmarIdPaquete(e.currentTarget.value);
            }}
            aria-label="Identificador único del paquete"
            aria-invalid={idInvalido}
            className={`${inputBase} w-24 font-mono`}
            style={
              idInvalido
                ? { ...inputStyle, borderColor: 'var(--avianca-red)', background: 'var(--red-soft)' }
                : inputStyle
            }
          />
        </label>
        {idInvalido && (
          <span className="text-[11px]" style={{ color: 'var(--avianca-red)' }} role="alert">
            Formato esperado: XX-123
          </span>
        )}

        {tierMeta && (
          <span
            className="inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium"
            style={{ background: tierMeta.bg, color: tierMeta.color, borderColor: tierMeta.border }}
            aria-label={`Tramo de impuesto del paquete: ${tierMeta.label}`}
          >
            {tierMeta.label}
          </span>
        )}

        <span className="ml-auto font-mono text-xs font-semibold" style={{ color: 'var(--text-main)' }}>
          Subtotal: {subtotal.toFixed(2)}
        </span>

        {/* Combinar con… */}
        {otrosPaquetes.length > 0 && (
          <span className="relative inline-flex items-center gap-1">
            <label className="sr-only" htmlFor={`combinar-${pkg.unique_package_id}`}>
              Combinar este paquete con otro
            </label>
            <select
              id={`combinar-${pkg.unique_package_id}`}
              value={confirmarCombinarCon}
              onChange={(e) => setConfirmarCombinarCon(e.target.value)}
              aria-label="Combinar este paquete con otro"
              className={`${inputBase} text-[11px]`}
              style={inputStyle}
            >
              <option value="">Combinar con…</option>
              {otrosPaquetes.map((p) => (
                <option key={p.unique_package_id} value={p.unique_package_id}>
                  {p.unique_package_id} — {p.recipient_name || 'sin nombre'}
                </option>
              ))}
            </select>
            {confirmarCombinarCon && (
              <button
                type="button"
                onClick={() => {
                  onMerge(pkg.unique_package_id, confirmarCombinarCon);
                  setConfirmarCombinarCon('');
                }}
                aria-label={`Confirmar combinación con ${confirmarCombinarCon}`}
                className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold
                           hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--amber)]"
                style={{ background: 'var(--amber-soft)', borderColor: 'var(--amber)', color: 'var(--amber)' }}
              >
                <Merge size={11} aria-hidden="true" /> Confirmar
              </button>
            )}
          </span>
        )}
      </div>

      {/* ── Encabezado de columnas ── */}
      <div
        className="grid grid-cols-[minmax(0,1fr)_104px_56px_88px_auto_72px_auto] gap-2 px-3 pt-2 text-[10px] font-semibold uppercase tracking-wide"
        style={{ color: 'var(--text-tertiary)' }}
        aria-hidden="true"
      >
        <span>Descripción</span>
        <span aria-hidden="true" />{/* celda vacía sobre el indicador de procedencia */}
        <span className="text-right">Cant.</span>
        <span className="text-right">Valor unit.</span>
        <span>Estado</span>
        <span className="text-right">Total</span>
        <span />
      </div>

      {/* ── Filas de artículos ── */}
      <div className="px-1.5 pb-1.5">
        {items.length === 0 && (
          <p className="px-3 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
            Este paquete todavía no tiene artículos. Agregue uno o pegue una fuente del chat.
          </p>
        )}
        {items.map((it) => (
          <div key={it.id} className="group relative">
            <ItemRow
              item={it}
              sourceTimestamp={timestampsPorMensaje[it.source_message_id]}
              onChange={(campo, valor) => actualizarItem(it.id, campo, valor)}
              onQuitar={() => quitarItem(it.id)}
              onEnterNewRow={agregarItemManual}
              quoteBuffer={quoteBuffer}
              onConsumeQuote={onConsumeQuote}
              onJumpToMessage={onJumpToMessage}
              onAskClient={onAskClient}
              autoFocus={it.id === ultimoAgregadoRef.current}
              isNew={it.id === ultimoAgregadoRef.current}
            />
            {/* Mover artículo a otro paquete */}
            {otrosPaquetes.length > 0 && (
              <div className="pl-2 pb-1">
                {menuMoverItemId === it.id ? (
                  <span className="inline-flex flex-wrap items-center gap-1 text-[11px]">
                    <span style={{ color: 'var(--text-secondary)' }}>Mover a:</span>
                    {otrosPaquetes.map((p) => (
                      <button
                        key={p.unique_package_id}
                        type="button"
                        onClick={() => {
                          setMenuMoverItemId(null);
                          onMoveItem(it.id, pkg.unique_package_id, p.unique_package_id);
                        }}
                        aria-label={`Mover artículo al paquete ${p.unique_package_id}`}
                        className="rounded-md border px-1.5 py-0.5 font-mono
                                   hover:bg-[var(--nav-active-bg)]
                                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
                        style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
                      >
                        {p.unique_package_id}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setMenuMoverItemId(null);
                        onMoveItem(it.id, pkg.unique_package_id, null);
                      }}
                      aria-label="Mover artículo a un paquete nuevo"
                      className="rounded-md border px-1.5 py-0.5
                                 hover:bg-[var(--nav-active-bg)]
                                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
                      style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
                    >
                      Nuevo paquete
                    </button>
                    <button
                      type="button"
                      onClick={() => setMenuMoverItemId(null)}
                      aria-label="Cerrar menú de mover artículo"
                      className="px-1 hover:underline"
                      style={{ color: 'var(--text-tertiary)' }}
                    >
                      Cerrar
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setMenuMoverItemId(it.id)}
                    aria-label={`Mover artículo ${it.description || 'sin descripción'} a otro paquete`}
                    className="inline-flex items-center gap-1 text-[10px] opacity-0 transition-opacity
                               group-hover:opacity-100 focus-visible:opacity-100
                               hover:underline focus-visible:outline-none"
                    style={{ color: 'var(--text-tertiary)' }}
                  >
                    <FolderInput size={10} aria-hidden="true" /> Mover artículo a…
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Agregar artículo manual ── */}
      <div className="border-t px-3 py-1.5" style={{ borderColor: 'var(--border)' }}>
        <button
          type="button"
          onClick={agregarItemManual}
          aria-label="Agregar artículo manual a este paquete"
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md px-2 text-xs font-medium
                     hover:bg-[var(--nav-active-bg)]
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
          style={{ color: 'var(--blue-text)' }}
        >
          <Plus size={14} aria-hidden="true" /> Agregar artículo
        </button>
      </div>
    </section>
  );
};

export default PackageBlock;

/*
Ejemplo de uso (dentro de WorkingEncomienda, ya ordenado por la regla del Anexo):
  <PackageBlock
    pkg={pkg}
    allPackages={draft.packages}
    customsConfig={draft.customs_config_snapshot}
    onChange={(pkgNuevo) => reemplazarPaquete(pkgNuevo)}
    onMoveItem={moverItem}
    onMerge={combinarPaquetes}
    quoteBuffer={quoteBuffer}
    onConsumeQuote={onConsumeQuote}
    onJumpToMessage={onJumpToMessage}
    onAskClient={onAskClient}
    timestampsPorMensaje={timestampsPorMensaje}
  />
*/
